/**
 * StandardSuggest
 *
 * Suggests a standard with original input text based on input text entered by user
 *  and/or standard selected by user.
 *
 * Combines a separate Autosuggest (StandardSuggestCore) suggestion with a ComboBox Select
 *   option into a single suggestion containing both a standard and originalText.
 *
 * useStandardSuggest provides control props for Subcomponents and methods to update state.
 *
 */

import React, { useEffect, useRef, forwardRef } from 'react'
import PropTypes from 'prop-types'
import { sanitizeProps } from '@fs/zion-frontend-friends'
import { useTranslation } from 'react-i18next'
import { NoticeWarning, ContentCheck } from '@fs/zion-icon'
import { colors, Row, Paragraph, BillboardText, Separator } from '@fs/zion-ui'
import { Link } from '@fs/zion-router'
import { css } from '@linaria/core'
import zionDebug from '@fs/zion-debug'
import { getDateEventByType, getPlaceEventByType, ValidEventTypes, EventTypeEnum } from './eventTypes'
import StandardSuggestCore from './StandardSuggestCore'
import PlaceSelect from '../place-suggest/PlaceSelect'
import DateSelect from '../date-suggest/DateSelect'
import { useStandardSuggest } from './useStandardSuggest'
import { fetchPlaceSuggestionsFromApi } from '../place-suggest/fetchPlaceSuggestions'

const debug = zionDebug('standard-suggest')

const negativeMargin = css`
  margin-top: -4px;
`

function getStandardSuggestCoreLabel({ type, eventType, label }) {
  if (eventType !== EventTypeEnum.NONE) {
    if (type === 'place') {
      return getPlaceEventByType(eventType)?.label
    }
    return getDateEventByType(eventType)?.label
  }
  return label
}

const StandardSuggest = forwardRef(function StandardSuggest(
  {
    type = 'place',
    eventType,
    onSuggestionSelected,
    externalLinkType = 'none',
    mapKey,
    initialSelectedItem,
    selectedItem,
    label,
    value,
    treeStandardUI,
    onIsOpenChange,
    ...props
  },
  ref
) {
  const { t } = useTranslation()
  const StandardSelect = type === 'place' ? PlaceSelect : DateSelect
  const { suggestions: initialSuggestions = [] } = props
  const {
    autoSuggestInitialSelectedItem,
    autoSuggestSelectedItem,
    autoSuggestValue,
    autoSuggestSuggestion,
    autoSuggestSuggestionIsStandard,
    autoSuggestInputText,
    standardSelectStandard,
    standardSelectSuggestions,
    standardSuggestSuggestion,
    selectAutoSuggestSuggestion,
    selectStandardSelectOption,
    changeInputText,
    selectItem,
    setStandardSelectSuggestions,
  } = useStandardSuggest({ selectedItem, onSuggestionSelected, initialSelectedItem, value, initialSuggestions, type })

  const notStandardizedWarningText =
    type === 'place'
      ? t('standard-suggest.non-standardized-place.warning', 'Non-standardized Place')
      : t('standard-suggest.non-standardized-date.warning', 'Non-standardized Date')
  const warningValidate =
    (autoSuggestSuggestion || autoSuggestInputText) && !standardSelectStandard ? notStandardizedWarningText : null
  const nonStandardizedWarning =
    !warningValidate &&
    (autoSuggestSuggestion || autoSuggestInputText) &&
    (!standardSelectStandard || !standardSelectStandard.isStandard)
      ? notStandardizedWarningText
      : null

  const handleSuggestionSelect = (suggestion, newSuggestions) => {
    selectAutoSuggestSuggestion({ suggestion, newSuggestions, t })
  }

  // We need to call onSuggestionSelected, when standard or originalText updates
  // but NOT when onSuggestionSelected updates (because ZionForm does it _every_
  // time the component renders). We can use a ref to solve our closure problems.
  const callback = useRef()
  callback.current = onSuggestionSelected
  useEffect(() => {
    callback.current?.(standardSuggestSuggestion)
  }, [standardSuggestSuggestion])

  const selectedItemRef = useRef()
  selectedItemRef.current = selectItem
  useEffect(() => {
    const select = selectedItemRef.current
    select && select(selectedItem)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem?.originalText, selectedItem?.standard])

  const {
    isExact = false,
    showRepIds = true,
    noCache = false,
    placeFilterOptions = {},
    hideUnsearchable = true,
  } = props
  const shouldShowStandardSelect = !autoSuggestSuggestionIsStandard && standardSelectSuggestions?.length > 1

  return (
    // This wrapper <div> is needed so that the StandardSuggest Component can place nicely inside flexbox containers (like Grid/Cell).
    <div>
      <StandardSuggestCore
        {...sanitizeProps(props)}
        type={type}
        label={getStandardSuggestCoreLabel({ type, eventType, label })}
        handleInput
        initialSelectedItem={autoSuggestInitialSelectedItem}
        onSuggestionSelected={(suggestion, newSuggestions) => handleSuggestionSelect(suggestion, newSuggestions)}
        selectedItem={autoSuggestSelectedItem}
        value={autoSuggestValue}
        warningValidate={() => !treeStandardUI && warningValidate}
        onInputTextChange={changeInputText}
        ref={ref}
      />
      <div className={!props?.dense ? negativeMargin : undefined}>
        {shouldShowStandardSelect && (
          <>
            {!treeStandardUI && standardSelectStandard?.standard && (
              <Paragraph size="xs">
                {t(type === 'date' ? 'standard-suggest.standard-date.label' : 'standard-suggest.standard-place.label')}
              </Paragraph>
            )}
            <StandardSelect
              standard={standardSelectStandard}
              externalLinkType={externalLinkType}
              mapKey={mapKey}
              suggestions={standardSelectSuggestions}
              isStandard={autoSuggestSuggestionIsStandard}
              warningValidate={() => warningValidate}
              onChange={selectStandardSelectOption}
              showDenseUI={treeStandardUI}
              downshiftProps={{
                onIsOpenChange: async ({ isOpen }) => {
                  if (!isOpen || !autoSuggestInputText || type !== 'place') return

                  try {
                    const suggestions = await fetchPlaceSuggestionsFromApi({
                      placesSearchTerm: autoSuggestInputText,
                      isExact,
                      // showRepIds historically defaults true for place suggestions
                      showRepIds,
                      noCache,
                      placeFilterOptions,
                      hideUnsearchable,
                    })
                    setStandardSelectSuggestions?.(suggestions)
                  } catch (error) {
                    debug('Error fetching suggestions:', error)
                  }
                },
              }}
            />
          </>
        )}
        {nonStandardizedWarning && !treeStandardUI && (
          <BillboardText primaryColor={colors.yellow60}>
            <Paragraph size="sm">
              <NoticeWarning /> {nonStandardizedWarning}
            </Paragraph>
          </BillboardText>
        )}
        {treeStandardUI && (standardSelectStandard || warningValidate) && (
          <>
            <Separator size="nano" />
            <StandardStatusDisplay type={type} isStandardized={standardSelectStandard?.standard} />
          </>
        )}
      </div>
    </div>
  )
})

export default StandardSuggest

StandardSuggest.propTypes = {
  /** An event handler called whenever the user changes input text or selects a standard */
  onChange: PropTypes.func,

  /** Type of StandardSuggest */
  type: PropTypes.oneOf(['date', 'place']),

  /** The event type */
  eventType: PropTypes.oneOf(ValidEventTypes),

  /** hideUnsearchable defaults to true. When set to false, it adds places to search results that are normally hidden (e.g. World) */
  hideUnsearchable: PropTypes.bool,

  /** The isExact option modifies the default search from "partial" to "exact" match.
   *  This "partial" search considers characters slightly misplaced as potential matches, enhancing search flexibility.
   */
  isExact: PropTypes.bool,

  /** Whether to include representation IDs in place suggestions */
  showRepIds: PropTypes.bool,

  /** - INTERNAL USE ONLY. Bypasses cache for fresh suggestions. */
  noCache: PropTypes.bool,

  /** Options to filter place suggestions. See PlaceSuggest for details. */
  placeFilterOptions: PropTypes.object,
}

function StandardStatusIndicator({ isStandardized, type }) {
  const [t] = useTranslation()
  const standardText =
    type === 'date'
      ? t('standard-suggest.standardized-date.label', 'Standardized Date')
      : t('standard-suggest.standardized-place.label', 'Standardized Place')

  const standardStatusText = isStandardized
    ? standardText
    : t('standard-suggest.none-standardized.warning', 'No matching standard')

  const DisplayedIcon = isStandardized ? ContentCheck : NoticeWarning
  const iconColor = isStandardized ? 'green' : 'orange'
  const textColor = isStandardized ? colors.green40 : colors.yellow40
  return (
    <BillboardText primaryColor={textColor}>
      <Paragraph size="xs">
        <DisplayedIcon size="sm" color={iconColor} />
        {standardStatusText}
      </Paragraph>
    </BillboardText>
  )
}

// This is required to align the bottom elements with the input field
const standardLabelWrapper = css`
  max-width: 80ch;
`

function StandardStatusDisplay({ type, isStandardized }) {
  const [t] = useTranslation()
  const linkUrl =
    type === 'date'
      ? 'https://www.familysearch.org/help/helpcenter/article/how-do-i-enter-dates-and-places-into-family-tree'
      : 'https://www.familysearch.org/help/helpcenter/article/how-do-i-request-a-new-place-in-the-database-of-standardized-places'
  return (
    <div className={standardLabelWrapper}>
      <Row guttersY="none">
        <StandardStatusIndicator type={type} isStandardized={isStandardized} growX />
        <Paragraph noShrinkX size="xs">
          <Link to={linkUrl} external target="_blank" wayfinding>
            {t('standard-suggest.what-is-this.link', "What's this?")}
          </Link>
        </Paragraph>
      </Row>
    </div>
  )
}
